'use client';

import { createContext, useState, useEffect, useContext, type PropsWithChildren, useCallback, useMemo } from 'react';
import type { EventLogger } from './models';
import { type ID, type LogParameters } from './models';
import { isClient } from '../function';
interface LoggerContextProps {
  isInitialized: boolean;
  debug: boolean;
  identify: (userId: ID, params: LogParameters) => void;
  group: (groupId: ID, params: LogParameters) => void;
  trackEvent: (event: string, params?: LogParameters) => void;
  metadata: LogParameters;
  appName: string;
}
const defaultAnalyticsContext: LoggerContextProps = {
  isInitialized: false,
  debug: false,
  identify: () => {
    // Default no-op implementation
  },
  group: () => {
    // Default no-op implementation
  },
  trackEvent: () => {
    // Default no-op implementation
  },
  metadata: {},
  appName: ''
};
const LoggerContext = createContext<LoggerContextProps>(defaultAnalyticsContext);
export const useLoggerRootContext = () => useContext(LoggerContext);
const getScreenInfo = () => {
  if (isClient() === false) {
    return {};
  }
  return {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight
  };
};
interface LoggerProviderProps {
  debug?: boolean;
  metadata?: LogParameters;
  appName: string;
  eventLogger: EventLogger;
}
export const LoggerProvider = ({
  children,
  debug = false,
  metadata,
  appName,
  eventLogger
}: PropsWithChildren<LoggerProviderProps>) => {
  const [screenMetadata, setScreenMetadata] = useState(getScreenInfo);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    setIsInitialized(true);
  }, []);
  const identify = useCallback((userId: ID, params: LogParameters = {}) => {
    if (debug) {
      console.info('[Debug Logger] Identify User:', {
        userId,
        ...params
      });
      return;
    }
    if (isClient() && eventLogger) {
      eventLogger({
        event: 'identify_user',
        params: {
          userId,
          ...params
        }
      });
    }
  }, [debug, eventLogger]);
  const group = useCallback((groupId: ID, params: LogParameters = {}) => {
    if (debug) {
      console.info('[Debug Logger] Group Identified:', {
        event: 'group_identified',
        groupId,
        ...params
      });
      return;
    }
    if (isClient() && eventLogger) {
      eventLogger({
        event: 'group_identified',
        params: {
          groupId,
          ...params
        }
      });
    }
  }, [debug, eventLogger]);
  const trackEvent = useCallback((event: string, params?: LogParameters) => {
    const eventData = {
      event,
      ...params,
      app_name: appName,
      ...metadata
    };
    if (debug) {
      console.info('[Debug Logger] Event:', event);
      console.table(eventData);
      return;
    }
    if (isClient()) {
      eventLogger({
        event: event,
        params: eventData
      });
    }
  }, [debug, appName, metadata, eventLogger]);
  const memoizedMetadata = useMemo(() => {
    return {
      ...metadata,
      ...screenMetadata
    };
  }, [screenMetadata, metadata]);
  useEffect(() => {
    if (isClient() === false) {
      return;
    }
    const resizeCallback = () => {
      setScreenMetadata(getScreenInfo());
    };
    window.addEventListener('resize', resizeCallback);
    return () => {
      window.removeEventListener('resize', resizeCallback);
    };
  }, []);
  const contextValue = useMemo(() => ({
    appName,
    isInitialized,
    debug: debug,
    identify,
    group,
    trackEvent,
    metadata: memoizedMetadata
  }), [appName, isInitialized, debug, identify, group, trackEvent, memoizedMetadata]);
  return <LoggerContext.Provider value={contextValue} data-sentry-element="LoggerContext.Provider" data-sentry-component="LoggerProvider" data-sentry-source-file="Logger.tsx">{children}</LoggerContext.Provider>;
};